// Felt-sæt for de fem multi-variant bidrag-flows på CarDetail.
// Hvert flow definerer rækker (felter) som vises i MultiVariantSuggestDialog.

import { ALL_MISSING_FIELDS, getRelevantFields, type MissingFieldDef } from './missing-fields';
import { EQUIPMENT_FIELD_KEYS, getRelevantEquipment, type EquipmentFieldDef } from './equipment-fields';

export type FlowFieldType = 'number' | 'text' | 'boolean' | 'select';
export type FlowFieldScope = 'variant' | 'specs' | 'price';

export interface FlowField {
  key: string;
  label: string;
  type: FlowFieldType;
  scope: FlowFieldScope;
  unit?: string;
  hint?: string;
  options?: { value: string; label: string }[];
}

// ---- Pris-flow ----
export const PRICE_FLOW_FIELDS: FlowField[] = [
  {
    key: 'price_dkk',
    label: 'Pris',
    type: 'number',
    scope: 'price',
    unit: 'kr.',
    hint: 'Vejledende udsalgspris i Danmark.',
  },
  {
    key: 'price_type',
    label: 'Pristype',
    type: 'select',
    scope: 'price',
    options: [
      { value: 'List', label: 'Listepris' },
      { value: 'From', label: 'Fra-pris' },
      { value: 'Campaign', label: 'Kampagnepris' },
      { value: 'LeaseExample', label: 'Leasingeksempel' },
    ],
  },
];

// ---- Tekniske specifikationer ----
export const TECH_SPECS_FLOW_FIELDS: FlowField[] = [
  { key: 'power_hp', label: 'Effekt', type: 'number', scope: 'specs', unit: 'hk' },
  { key: 'torque_nm', label: 'Drejningsmoment', type: 'number', scope: 'specs', unit: 'Nm' },
  { key: 'acceleration_0_100', label: '0-100 km/t', type: 'number', scope: 'specs', unit: 'sek' },
  { key: 'top_speed_kmh', label: 'Topfart', type: 'number', scope: 'specs', unit: 'km/t' },
  { key: 'battery_kwh', label: 'Batteri (netto)', type: 'number', scope: 'specs', unit: 'kWh' },
  { key: 'batteri_brutto_kwh', label: 'Batteri (brutto)', type: 'number', scope: 'specs', unit: 'kWh' },
  { key: 'range_wltp_km', label: 'WLTP-rækkevidde', type: 'number', scope: 'specs', unit: 'km' },
  { key: 'realistisk_vinterraekkevidde_km', label: 'Vinterrækkevidde', type: 'number', scope: 'specs', unit: 'km' },
  { key: 'motorvejsraekkevidde_km', label: 'Motorvejsrækkevidde', type: 'number', scope: 'specs', unit: 'km' },
  { key: 'consumption_wh_km', label: 'Elforbrug', type: 'number', scope: 'specs', unit: 'Wh/km' },
  { key: 'charging_ac_kw', label: 'AC-opladning', type: 'number', scope: 'specs', unit: 'kW' },
  { key: 'charging_dc_kw', label: 'DC-opladning', type: 'number', scope: 'specs', unit: 'kW' },
  { key: 'dc_charge_10_80_min', label: 'Ladetid 10-80%', type: 'number', scope: 'specs', unit: 'min' },
  { key: 'stiktype', label: 'Stiktype', type: 'text', scope: 'specs' },
  { key: 'batterikemi', label: 'Batterikemi', type: 'text', scope: 'specs' },
];

// ---- Køretøjsdata ----
export const VEHICLE_DATA_FLOW_FIELDS: FlowField[] = [
  { key: 'seats', label: 'Antal sæder', type: 'number', scope: 'variant' },
  { key: 'max_seats', label: 'Maks. sæder', type: 'number', scope: 'variant' },
  { key: 'extra_seats_optional', label: 'Ekstra sæder er tilvalg', type: 'boolean', scope: 'variant' },
  { key: 'doors', label: 'Antal døre', type: 'number', scope: 'variant' },
  { key: 'trunk_l', label: 'Bagagerum', type: 'number', scope: 'variant', unit: 'L' },
  { key: 'frunk_liter', label: 'Frunk', type: 'number', scope: 'variant', unit: 'L' },
  { key: 'curb_weight_kg', label: 'Egenvægt', type: 'number', scope: 'variant', unit: 'kg' },
  { key: 'tow_kg_braked', label: 'Trækvægt (bremset)', type: 'number', scope: 'variant', unit: 'kg' },
  { key: 'tow_kg_unbraked', label: 'Trækvægt (ubremset)', type: 'number', scope: 'variant', unit: 'kg' },
  { key: 'length_m', label: 'Længde', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'width_m', label: 'Bredde', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'height_m', label: 'Højde', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'wheelbase_m', label: 'Akselafstand', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'turning_diameter_m', label: 'Vendediameter', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'warranty_years', label: 'Garanti', type: 'number', scope: 'variant', unit: 'år' },
  { key: 'warranty_km', label: 'Garanti', type: 'number', scope: 'variant', unit: 'km' },
  { key: 'battery_warranty_years', label: 'Batterigaranti', type: 'number', scope: 'variant', unit: 'år' },
  { key: 'battery_warranty_km', label: 'Batterigaranti', type: 'number', scope: 'variant', unit: 'km' },
  { key: 'euro_ncap', label: 'Euro NCAP', type: 'text', scope: 'variant' },
  { key: 'min_wheel_size_inches', label: 'Min. fælgstørrelse', type: 'number', scope: 'variant', unit: '"' },
  { key: 'green_owner_tax_yearly_dkk', label: 'CO₂-ejerafgift', type: 'number', scope: 'variant', unit: 'kr./år' },
];

// ---- Manglende felter (hele relevante mangler-sæt ekskl. equipment) ----
function missingFieldToFlowField(f: MissingFieldDef): FlowField {
  return {
    key: f.key,
    label: f.label,
    type: f.type === 'date' ? 'text' : f.type,
    scope: f.scope,
    unit: f.unit,
    hint: f.hint,
  };
}

export function getMissingFlowFields(powertrainType: string | null | undefined): FlowField[] {
  return getRelevantFields(powertrainType)
    .filter(f => !EQUIPMENT_FIELD_KEYS.has(f.key))
    .map(missingFieldToFlowField);
}

// ---- Standardudstyr-flow ----
function equipmentFieldToFlowField(f: EquipmentFieldDef): FlowField {
  return {
    key: f.key,
    label: f.label,
    type: f.type ?? 'boolean',
    scope: f.scope,
    unit: f.unit,
  };
}

export function getEquipmentFlowFields(powertrainType: string | null | undefined): FlowField[] {
  return getRelevantEquipment(powertrainType).map(equipmentFieldToFlowField);
}

// Flow-titel-helper
export type FlowKind = 'price' | 'tech' | 'vehicle' | 'missing' | 'equipment';

export function getFlowTitle(kind: FlowKind): string {
  switch (kind) {
    case 'price': return 'Foreslå priser på tværs af varianter';
    case 'tech': return 'Foreslå tekniske specifikationer';
    case 'vehicle': return 'Foreslå køretøjsdata';
    case 'missing': return 'Hjælp os med manglende data';
    case 'equipment': return 'Bekræft standardudstyr';
  }
}

export function getFlowFields(kind: FlowKind, powertrainType: string | null | undefined): FlowField[] {
  switch (kind) {
    case 'price': return PRICE_FLOW_FIELDS;
    case 'tech': return TECH_SPECS_FLOW_FIELDS;
    case 'vehicle': return VEHICLE_DATA_FLOW_FIELDS;
    case 'missing': return getMissingFlowFields(powertrainType);
    case 'equipment': return getEquipmentFlowFields(powertrainType);
  }
}

// Re-eksport for nem brug
export { ALL_MISSING_FIELDS };
